import {
  ColumnId,
  DataTableProps,
  EmptyStateProps,
  SortableTableProps,
  SimplePaginationProps,
  TableStyleProps,
} from '@dixa/lib-shared-habla';
import type {
  SearchConversationEnriched,
  SearchSortField,
} from 'lib-search-types';
import { AsyncStatus } from 'lib-shared-types-api';
import type { ReactNode } from 'react';

import type { UseConversationsTableLayoutResult } from './utils/useConversationsTableLayout';

export type ConversationsTableProps = {
  conversations: readonly SearchConversationEnriched[];
  asyncStatus: AsyncStatus;
  selectableRowsProps?: DataTableProps<
    SearchConversationEnriched,
    never
  >['selectableRowsProps'];
  paginationProps?: SimplePaginationProps;
  sortingProps?: SortableTableProps;
  /** Overrides the default empty state shown when there are no conversations. */
  emptyStateProps?: Partial<EmptyStateProps>;
  /** When provided, replaces the default navigation on row click. */
  onRowClick?: (conversation: SearchConversationEnriched) => void;
  /** The id of the conversation whose row should be highlighted, e.g. when it's being previewed. */
  highlightedRowId?: number;
  /** Whether the user can choose the columns, saved to their user storage. */
  isCustomizable?: boolean;
} & TableStyleProps;

export type ConversationsTableSortableField =
  (typeof conversationsTableSortableFields)[number];

export const conversationsTableSortableFields = [
  'created_at',
  'last_message_created_at',
] satisfies SearchSortField<'conversations'>[];

/** How a conversations table column is rendered with the Habla table. */
export type ConversationsTableColumnMeta = {
  /** The name of the column in the table preferences; columns without one aren't listed. */
  readonly label?: string;
  /** The width of the column header cell. */
  readonly width?: string;
  /** The id the table sorts by when the header is clicked. */
  readonly sortBy?: ColumnId;
  /** The tooltip shown on the column header. */
  readonly tooltip?: string;
  /** What the column shows before the first load. */
  readonly placeholder?: {
    /** The width of the header placeholder. */
    readonly header?: string;
    /** The content of the placeholder rows' cells. */
    readonly cell?: ReactNode;
  };
};

/** How much space the rows of the conversations table take up. */
export type ConversationsTableDensity = 'default' | 'compact';

/** What the conversations table passes to its header and cell renderers. */
export type ConversationsTableMeta = {
  /** The user's saved layout, when they can customise the table. */
  readonly layout?: UseConversationsTableLayoutResult;
};
